import type { UseFetchOptions } from '#app';
import type { ISbStory, ISbStoryParams } from 'storyblok-js-client';
import { postStoryblokV3CdnGet, type PostStoryblokV3CdnGetBody } from '@tapp/api';
import type { useApiFetch } from '@tapp/api/src/composables/useApiFetch';
import { useSsrCacheHeaders } from '@tapp/api/src/composables/useSsrCacheHeaders';

import { getParams, getIsCacheDisabled } from '../utils';
import useLiveStory from './useLiveStory';

interface UseStoryFetchOptions {
  path?: string;
  params?: ISbStoryParams;
  locale?: string;
  metaData?: Record<string, unknown>;
}

export default async function useStoryFetch<T>(
  { path, params, locale, metaData }: UseStoryFetchOptions,
  { ssrCache, headers, ...options }: UseFetchOptions<ISbStory<T>> & { ssrCache?: boolean } = {},
) {
  const route = useRoute();
  const storySlug = `stories${path || '/' + route.query.url}`;
  // Kept in the pre-migration URL form so the useAsyncData cache key below stays identical.
  const storyPath = `/front-office/v3/storyblok/cdn/get/${storySlug}`;
  const disableCache = getIsCacheDisabled();

  const useFetchResult = postStoryblokV3CdnGet(
    storySlug,
    // Callers pass the back-end's snake_case keys; the generated type spells them camelCase.
    { meta: metaData } as PostStoryblokV3CdnGetBody,
    {
      params: getParams(params, locale),
      headers: {
        ...(disableCache && { 'X-Storyblok-No-Cache': 'true' }),
        ...useSsrCacheHeaders(ssrCache),
        // @info keep this in order as the last item, so it can be used as override!
        ...(headers as Record<string, string> | undefined),
      },
      // Storyblok content is read verbatim (`full_slug`, `_uid`), so keep the camelCasing off.
      transform: (data: unknown) => data,
      getCachedData(key: string, nuxtApp: ReturnType<typeof useNuxtApp>, { cause }: { cause: string }) {
        if (cause === 'refresh:hook') {
          return undefined;
        }

        return nuxtApp.payload.data[key] || undefined;
      },
      key: options.key || `storyblok-${storyPath}-${JSON.stringify(metaData)}`,
      ...options,
    },
    // The spec leaves `data` opaque, so the generated type is useless here; Storyblok's is accurate.
  ) as unknown as ReturnType<typeof useApiFetch<ISbStory<T>>>;

  if (!isInStoryblokVisualEditor()) {
    return useFetchResult;
  }

  const result = await useFetchResult;
  const originalStory = result.data.value;
  const liveStory = useLiveStory(originalStory?.data.story, {
    resolveRelations: params?.resolve_relations,
    metaData,
  });

  return {
    ...result,
    data: computed(() =>
      originalStory && liveStory.value
        ? { ...originalStory, data: { ...originalStory.data, story: liveStory.value } }
        : originalStory,
    ),
  };
}
